import {
  IObservableArray,
  isObservableArray,
  makeAutoObservable,
  observable,
} from "mobx";
import { Domino, Slot } from "../TaskWindows/Domino/DominoesMassive";
import dominoConverter from "../TaskWindows/Domino/DominoesMassive";
import { cloneDeep } from "lodash";
import { shuffleArrayElements } from "../TaskWindows/Domino/Shuffling";

interface IDominoesStore {
  appendDominoToSlot(newDomino: Domino): void;
  deleteDominoFromHome(dominoId: number): void;
  appendDominoToHome(newDomino: Domino): void;
  deleteDominoFromSlot(dominoId: number): void;

  getDominoById(dominoId: number): Domino | never;
  getDominoByData(
    firstComponent: string,
    secondComponent: string
  ): Domino | never;
  getSlotById(slotId: number): Slot | never;
  getFirstFreeSlot(): Slot | never;
  moveDominoToSlot(dominoId: number): void;
  checkCorrectDomino(slot: Slot, dominoId: number): boolean;
  moveDominoToHome(slotId: number): void;
}

export class DominoesStore implements IDominoesStore {
  private words: string[] = [];

  public dominoes: IObservableArray<Domino> = observable.array([]);
  public originalDominoes: Domino[] = [];
  public slots: IObservableArray<Slot> = observable.array([]);
  public firstDomino: Domino;
  public lastDomino: Domino;

  /**Creation a massive of dominoes*/
  private createDominoes(): Domino[] {
    return dominoConverter.createDominoes(this.words);
  }

  /** Creation a massive of slots*/
  private createSlots(): Slot[] {
    return this.originalDominoes.map(
      (d: Domino) =>
        new Slot({
          id: d.id,
          firstComponent: " ",
          secondComponent: " ",
        })
    );
  }

  /** Creation an original massive of dominoes*/
  private createOriginalDominoes(): Domino[] {
    return cloneDeep(this.dominoes);
  }

  /** Creation an observable arrays  */
  private initializeObservableArrays(): void {
    this.dominoes = observable.array(this.createDominoes());

    this.originalDominoes = this.createOriginalDominoes();

    this.slots = observable.array(this.createSlots());
  }

  constructor(wordList: string[]) {
    this.words = wordList;
    makeAutoObservable(this);
    this.initializeObservableArrays();
    this.firstDomino = cloneDeep(this.originalDominoes[0]);

    this.lastDomino = cloneDeep(
      this.originalDominoes[this.originalDominoes.length - 1]
    );
    shuffleArrayElements(this.dominoes);
    this.originalDominoes = cloneDeep(this.dominoes);
  }

  /**Appending domino to slot */
  public appendDominoToSlot(newDomino: Domino): void {
    // Getting first slot's id
    let slotId: number = 0;
    try {
      slotId = this.getFirstFreeSlot().id;
    } catch (error) {
      alert(error);
    }
    // Deleting a slot by id

    this.slots.replace(this.slots.filter((elem: Slot) => elem.id !== slotId));

    let newSlot: Slot = new Slot({
      id: slotId,
      firstComponent: newDomino.firstComponent,
      secondComponent: newDomino.secondComponent,
    });

    newSlot.isFree = false;
    if (this.checkCorrectDomino(newSlot, newDomino.id)) {
      this.originalDominoes = this.originalDominoes.filter(
        (elem: Domino) => elem.id !== newDomino.id
      );
    }

    this.slots.push(newSlot);
    this.orderingSlots();
  }

  /**Ordering slots */
  private orderingSlots(): void {
    this.slots = this.slots.sort((elem1: Slot, elem2: Slot) =>
      elem1.id < elem2.id ? -1 : 1
    );
  }

  /**Deleting domino from home */
  public deleteDominoFromHome(dominoId: number): void {
    this.dominoes.replace(
      this.dominoes.filter((elem: Domino) => elem.id !== dominoId)
    );
  }

  /**Appending domino to home */
  public appendDominoToHome(newDomino: Domino): void {
    this.dominoes.push(
      new Domino({
        id: this.originalDominoes.length + 1,
        firstComponent: " ",
        secondComponent: " ",
      })
    );   

    const indexToSwipe: number = this.dominoes.findIndex(
      (element: Domino, index: number) =>
        element.id !== this.originalDominoes[index].id
    );

    
    let tempDomino: Domino = newDomino;
    let copyDomino: Domino;

    for (let j: number = indexToSwipe; j < this.dominoes.length; j += 1) {
      copyDomino = cloneDeep(this.dominoes[j]);
      this.dominoes[j] = cloneDeep(tempDomino);
      tempDomino = cloneDeep(copyDomino);
    }
  }

  /**Deleting domino from slot */
  public deleteDominoFromSlot(slotId: number): void {
    this.slots.replace(this.slots.filter((elem: Slot) => elem.id !== slotId));

    this.slots.push({
      id: slotId,
      firstComponent: " ",
      secondComponent: " ",
      isFree: true,
      isCorrect: false,
    });

    this.orderingSlots();
  }

  /**  Getting domino by id*/
  public getDominoById(dominoId: number): Domino | never {
    const foundDomino: Domino | undefined = this.originalDominoes.find(
      (element: Domino) => element.id === dominoId
    );

    if (foundDomino !== undefined) {
      return foundDomino;
    } else {
      throw new Error("Domino is not found!");
    }
  }

  /**  Getting domino by data */
  public getDominoByData(
    firstComponent: string,
    secondComponent: string
  ): Domino | never {
    const foundDomino = this.originalDominoes.find(
      (element: Domino) =>
        element.firstComponent === firstComponent &&
        element.secondComponent === secondComponent
    );

    if (foundDomino !== undefined) {
      return foundDomino;
    } else {
      throw new Error("Domino is not found!");
    }
  }

  /** Getting slot by id */
  public getSlotById(slotId: number): Slot | never {
    const foundSlot: Slot | undefined = this.slots.find(
      (element: Slot) => element.id === slotId
    );

    if (foundSlot !== undefined) {
      return foundSlot;
    } else {
      throw new Error("Slot is not found!");
    }
  }

  /**Getting a first free slot */
  public getFirstFreeSlot(): Slot | never {
    const foundSlot: Slot | undefined = this.slots.find(
      (elem: Slot) =>
        elem.firstComponent === " " && elem.secondComponent === " "
    );

    if (foundSlot !== undefined) {
      return foundSlot;
    } else {
      throw new Error("Slot is not found!");
    }
  }

  /**  Moving domino to slot*/
  public moveDominoToSlot(dominoId: number): void {
    this.deleteDominoFromHome(dominoId);

    try {
      this.appendDominoToSlot(this.getDominoById(dominoId));
    } catch (error) {
      alert(error);
    }
  }

  /**  Checking whether the domino is correct*/
  public checkCorrectDomino(slot: Slot, dominoId: number): boolean {
    if (slot.id === dominoId) {
      slot.isCorrect = true;
      return true;
    }

    return false;
  }

  /**  Moving domino to home*/
  public moveDominoToHome(slotId: number): void {
    let slot: Slot;
    try {
      slot = this.getSlotById(slotId);

      this.appendDominoToHome(
        slot.isCorrect
          ? this.getDominoById(slotId)
          : this.getDominoByData(slot.firstComponent, slot.secondComponent)
      );
    } catch (error) {
      alert(error);
    }

    this.deleteDominoFromSlot(slotId);
  }
}

